export interface CreateContractRequest {
  merchantId?: string | null;
  contractNumber: string;
  merchantExternalId?: string | null;
  product?: any | null;
}

export interface DisbursementDto {
  disbursementId: string;
  contractId: string;
  createdDate: Date;
  principal: number;
  interest: number;
  dailyCollectionAmount: number;
  firstCollection: Date;
  type: string;
  amountOutstanding: number;
  /** What the supplier sells, on a 3rd party disbursement (work item 926). */
  supplierTypeId?: number | null;
  supplierType?: string | null;
  supplierTypeName?: string | null;
}

export interface PaymentsAllocationDto {
  paymentAllocationId?: string;
  paymentId: string;
  disbursementId: string;
  amount: number;
  allocatedOnUtc?: string;
}

export interface TransactionDto {
  contractId: string;
  transactionId: string;
  createdDate: Date;
  description: string;
  amount: number;
  balance: number;
}

export interface CollectionDto {
  collectionId: string;
  contractId: string;
  billingCycle: string;
  amount: number;
  createdDate: Date;
  status: CollectionStatus;
  comment?: string | null;
  missedCollections: number;
}

export interface ContractDto {
  contractId: string;
  contractNumber: string;
  principle: number;
  interest: number;
  merchantId: string;
  dailyCollectionAmount: number;
  balance: number;
  createdDate: Date;
  firstCollection: string;
  missedCollections: number;
  maxDisbursement: number;
  status: ContractStatus;
  disbursements: DisbursementDto[];
  transactions: TransactionDto[];
  collections: CollectionDto[];
  payments: PaymentDto[];
  merchant: MerchantDto;
  product: ProductDto;
  disbursementHoldEnabled: boolean;
  applicationId: string;
}

export enum CollectionStatus {
  Pending = "pending",
  Success = "success",
  Failed = "failed",
  InsufficientFunds = "insufficientFunds",
}

export enum ContractStatus {
  Active = "Active",
  Inactive = "Inactive",
  Closed = "Closed",
  Settled = "Settled",
  Restructured = "Restructured",
}

export interface AddressDto {
  streetNumber: string;
  streetName: string;
  suburb: string;
  town: string;
  region: string;
  province: string;
  postalCode: string;
  latitude: number;
  longitude: number;
}

export interface PersonDto {
  personId: string;
  name: string;
  surname: string;
  identityNumber: string;
  mobileNumber: string;
  emailAddress?: string;
  whatsAppNumber?: string;
  alternatePhoneNumber?: string;
  nextOfKinName?: string;
  nextOfKinPhoneNumber?: string;
  nextOfKinRelationship?: string;
  gender?: string;
  race?: string;
  disabilities?: string[];
  idCardFrontFile?: string;
  idCardBackFile?: string;
  selfieFile?: string;
}


export enum BankAccountType
{
  Current = "current",
  Savings = "savings"
}

export interface BankAccountDto {
  accountHolderName: string;
  accountNumber: string;
  /** POPIA-safe display value returned by the Wallet API: first 2 + last 3 digits, middle masked. */
  maskedAccountNumber?: string;
  branchCode: string;
  accountType: BankAccountType;
  bankName: string;
}

export interface Equipment {
  serialNumber: string;
}

export enum EquipmentState {
  InUse = 'In Use'
}

export interface EquipmentPosScreen extends Equipment {
  fileId?: string;
  state: EquipmentState;
}

export interface EquipmentCommsBox extends Equipment {
  fileId?: string;
  state: EquipmentState;
  network: string;
}

export interface EquipmentList {
  posScreens: EquipmentPosScreen[];
  commsBoxes: EquipmentCommsBox[];
  cardMachines: Equipment[];
  barcodeScanners: Equipment[];
  tillDrawers: Equipment[];
}



export interface MerchantDto {
  merchantId: string;
  name: string;
  externalReference: string;
  address?: AddressDto;
  bankAccount?: BankAccountDto;
  owner?: PersonDto;
  equipment?: EquipmentList;
  storeType?: string;
  storeRegistrationNumber?: string;
  storePhotoFiles: string[];
  fireCertificateFile?: string;
  municipalLicenceFile?: string;
  healthCertificateFile?: string;
  businessAssociationMemberships?: string;
}


export interface SupplierDto {
  supplierId: string;
  supplierCode: string;
  name: string;
  emailAddress: string;
  phoneNumber: string;
  address?: AddressDto;
  bankAccount?: BankAccountDto;
  createdDate: Date;
  iqAccountNumber: string;
  /**
   * What the supplier sells; filled in on its third-party disbursements (work item 792). A
   * maintained list since work item 926 (the fixed "disbursement type" before). The backoffice
   * saves by id; the code and name come back for display.
   */
  supplierTypeId?: number | null;
  supplierType?: string | null;
  supplierTypeName?: string | null;
  /** Whether the bank account has been reviewed (work item 790). */
  bankAccountVerificationStatus?: BankAccountVerificationStatus;
  /** The stored AVSR report for the current bank account, when one was run. */
  bankAccountAvsrFileId?: string | null;
}

/** Review state of a bank account; the API sends the enum names as they are. */
export enum BankAccountVerificationStatus {
  Unverified = "Unverified",
  Verified = "Verified",
  Declined = "Declined",
}

export enum BankTransferStatus
{
  New = "new",
  Approved = "approved",
  Sent = "sent",
  Completed = "completed",
}

export interface BankTransfer {
  bankTransferId: string,
  sourceContractId: string,
  disbursementId: string,
  merchantId: string,
  disbursementType: DisbursementType,
  amount: number,
  createdDate: Date,
  destinationBankAccount: BankAccountDto,
  status: BankTransferStatus,
  supplierId?: string,
}

export interface ProductDto {
  name: string;
  repaymentPeriodDays: number;
  maxNumberOfClawbackDays: number;
  interestRate: number;
  gracePeriodToFirstCollection: number;
  loanValue: number;
}

export enum DisbursementType {
  Wallet = "wallet",
  Claim = "claim",
  ThirdParty = "3rdParty",
  Refund = "refund",
  MerchantBank = "merchantBank"
}

/** A supplier type from the maintained list (work item 926). */
export interface SupplierTypeDto {
  supplierTypeId: number;
  /** Stable code API callers send; never changes, even on a rename. */
  code: string;
  name: string;
  /** Inactive types stay on existing records but are not offered for new choices. */
  isActive: boolean;
  supplierCount: number;
  disbursementCount: number;
  /** Something refers to the type, so it can only be deactivated, not deleted. */
  inUse: boolean;
}

export interface DisbursementRequestDto {
  contractId: string;
  amount: number,
  type: DisbursementType;
  /** Supplier type code for a 3rd party disbursement (the API still accepts the old thirdPartyDisbursementType). */
  supplierType?: string | null;
  supplierId?: string | null
}

export interface CollectionStatusUpdateDto {
  contractId: string;
  status: CollectionStatus;
  billingCycle: string;
}

export interface StockOnCreditSummary {
  amountQualifiedFor: number;
  amountUsed: number;
  amountAvailable: number;
  currentBalance: number;
  arrears: number;
}

export enum ApplicationStatus {
  New = "new",
  Pending = "pending",
  Approved = "approved",
  Declined = "declined",
}

export interface ApplicationIncomeAndExpensesDto
{
  amount: number,
  description: string,
  isIncome: boolean
}

export interface ApplicationDto {
  applicationId: string;
  creationDate: Date;
  statusChangeDate: Date;
  merchantId: string;
  status: ApplicationStatus;
  merchantVerified: boolean;
  merchantAgreeToCreditCheck: boolean;
  merchantAgreeToAffordabilityCheck: boolean;
  sendTermsAndConditions: boolean;
  termsAndConditionsUnderstood: boolean;
  termsAndConditionsAccepted: boolean;
  incomeAndExpenses: ApplicationIncomeAndExpensesDto[];
  maxLoanAmount: number;
  productCode: string;
  merchant: MerchantDto;
  contractId: string | null;
  verificationFailedReason: string | null;
  preApprovalDocumentHtmlFile: string;
  preApprovalDocumentPdfFile: string;
  workflowItemId: string;
  idvPhotoFileUrl: string;
  ficFileUrl: string;
  creditReportFileUrl: string;
  agreementDocumentHtmlFile: string | null;
  agreementDocumentPdfFile: string | null;
  kycFileId: string | null;
  ficSolveFileId: string | null;
  pepScreeningFindings: string | null;
}

export interface ApplicationInitiationModel {
  application: ApplicationDto;
  summary: StockOnCreditSummary;
  isActivated: boolean;
  contractId: string;
}

export interface CommonLibraryEnvironment {
  apiUrl: string;
  apiWalletUrl: string;
  /**
   * Where the server logs are, for the "View server logs" link in the transaction log popup
   * (work items 704 and 924). Set in production only; where it is absent the link is hidden.
   */
  logAnalytics?: LogAnalyticsSettings;
}

/**
 * Same shape as the one in shared/utils/logs-deep-link. Declared here rather than imported
 * because a2pay.mobile copies this folder on its own, and an import from outside it breaks
 * the mobile build (QA frontend run 1476).
 */
export interface LogAnalyticsSettings {
  /** ARM id of the Log Analytics workspace. Not a secret. */
  workspaceResourceId: string;
  /** Entra tenant id, so guests land in the right directory. */
  tenantId?: string;
}

export interface AcceptApplicationRequest{
  product: any
}

export interface VerificationInfo
{
  ficUrl: string,
  creditReportUrl: string,
  idvPhotoUrl: string
}

export interface SendOtpResult
{
  success: boolean,
  otpId: string,
  expirationDate: Date
}

export enum VerifyOtpStatus
{
    Success = "Success",
    Invalid = "Invalid",
    Expired = "Expired",
    MaxAttemptsReached = "MaxAttemptsReached",
}

export interface VerifyOtpResult {
  status: VerifyOtpStatus;
  expirationDate: Date;
  otpId: string;
  remainingAttempts: number;
}

export interface UserDto
{
  userId: string,
  subject: string,
  nickname: string,
  name: string,
  picture: string,
  email: string,
  emailVerified: boolean,
  createdUtc: Date,
  roles: string[],
  permissions: string[],
  effectivePermissions: string[],
}

export interface PermissionDto
{
  name: string,
  description: string
}

export interface RoleDto
{
  name: string,
  description: string
  permissions: PermissionDto[]
}

export enum MerchantEnrolmentRole {
  Merchant = 'Merchant',
  FieldAgent = 'FieldAgent',
  NewMerchant = 'NewMerchant',
  ExistingMerchant = 'ExistingMerchant'
}

export interface MerchantEnrolmentDto {
  merchantEnrolmentId: string;
  completedByMerchantEnrolmentRole: MerchantEnrolmentRole;
  assistedByRepresentative: boolean;
  assistedByUserId?: string;
  completedByOwner: boolean;
  completedFromOnSite: boolean;
  applyingForStockOnCredit: boolean;
  agreeToPosMaintenancePlan: boolean;
  agreeToPosLicenseFee: boolean;
  businessPremiseOwned: boolean;
  hasMunicipalLicense: boolean;
  needsAssistanceWithMunicipalLicense: boolean;
  hasFireCertificate: boolean;
  storeIsCipcRegistered: boolean;
  needsAssistanceWithCipcRegistration: boolean;
  interestedInWalletTopUpViaDebitCard: boolean;
  hasHealthCertificate: boolean;
  belongsToBusinessAssociation: boolean;
  additionalInformation?: string;
  signatureFile?: string;
  merchantDetails?: MerchantDto;
}

export interface LookupDto
{
  key: string,
  value: string,
}

export enum LookupType
{
  Gender = "gender",
  Race = "race",
  StoreType = "storeType",
  Province = "province",
  NextOfKinRelationship = "nextOfKinRelationship",
  FieldAgent = "fieldAgent",
  MobileNetwork = "mobileNetwork",
  Bank = "bank",
  ClaimVerificationUsers = "claimVerificationUsers",
  ClaimApprovalUsers = "claimApprovalUsers"
}

export interface FileDto {
  fileId: string;
  url: string;
  fileName: string;
  size: number;
  contentType: string;
  createdUtc: string;
  uploadedBy: string | null;
}

export interface ProblemDto
{
  detail: string,
  status: number,
  title: string,
  type: string
}


export interface PerformWorkflowUserAction {
  workflowItemId: string;
  stepId: string;
  actionName: string;
  comment?: string | null;
  /** Values for the action's optional fields, keyed by field key (work item 903). */
  fields?: Record<string, string>;
}

/** An optional input that goes with a workflow action (work item 903). */
export interface WorkflowActionField {
  key: string;
  label: string;
  type: 'Text' | 'Checkbox';
  maxLength: number;
  hint?: string | null;
}

export interface WorkflowItemHistoryDto {
  workflowItemId: string;
  workflowItemHistoryId: string;
  userId: string;
  createdUtc: string;
  stepId: string;
  stepName: string;
  isSystemAction: boolean;
  actionName?: string | null;
  comment?: string | null;
}

export enum WorkflowItemStatus {
  Active,
  Completed,
  Cancelled,
  Faulted
}

export interface WorkflowItemDto {
  workflowItemId: string;
  dataType: string;
  workflowDefinitionName: string;
  currentStepId: string;
  assignedGroup: string;
  assignedUser?: string | null;
  createdUtc: string;
  updatedUtc?: string | null;
  lastUpdatedBy?: string | null;
  createdBy: string;
  previousAction?: string | null;
  previousStep?: string | null;
  isSystemAction: boolean;
  currentUserActions: string[];
  visibleUserActions: string[];
  status: WorkflowItemStatus;
  error?: string | null;
  history: WorkflowItemHistoryDto[];
  data: any;
  stepName?: string | null;
  stepDescription?: string | null;
  /** Plain-language explanation of a known wallet onboarding fault; absent otherwise. */
  diagnosis?: { code: string; message: string; suggestedField: string | null } | null;
  /** Actions on the current step that the API refuses without a comment (work item 902). */
  commentRequiredActions?: string[];
  /** Optional inputs each action on the current step takes, keyed by action name (work item 903). */
  actionFields?: Record<string, WorkflowActionField[]>;
  /** The most recent decision a person took on the item; null when no one has acted yet. */
  lastUserDecision?: WorkflowUserDecisionDto | null;
  /** The merchant's ID number masked by the API (`800101 **** **7`); set on list results (work item 904). */
  maskedIdNumber?: string | null;
}

/** A decision a person took on a workflow item. */
export interface WorkflowUserDecisionDto {
  actionName: string;
  comment?: string | null;
  userId: string;
  /** Resolved on the single-item view only. */
  userName?: string | null;
  decidedUtc: string;
}

export enum WorkflowDefinitions
{
  ApplicationWorkflow = "Application Processing",
  MerchantEnrolment = "Merchant Verification",
  Claims = "Claim"
}

export enum ClaimType {
    InvoiceClaim = "InvoiceClaim",
    SupplierPayment = "SupplierPayment"
}

export enum SupplierPaymentApprovalStatus {
    Pending = "Pending",
    Approved = "Approved",
    Revised = "Revised",
    Declined = "Declined",
}

export interface ClaimDto {
    claimId: string;
    tansactionId?: string;
    createdUtc: Date;
    externalReference?: string;
    storeName?: string;
    ownerName?: string;
    ownerSurname?: string;
    ownerWhatsAppNumber?: string;
    storeType?: string;
    ownerEmailAddress?: string;
    claimType: ClaimType;
    supplierName?: string;
    supplierBankingConfirmationLetterFileId?: string;
    invoiceNumber?: string;
    invoiceDate: Date;
    invoiceAmount: number;
    receiptFileIds: string[];
    receiptTexts: string[];
    declaration: boolean;
    ownerSignatureFileId?: string;
    transactionVerified: boolean;
    transactionVerifiedDate: Date;
    verificationComments?: string;
    creditAvailable: number;
    verifiedByUserId?: string;
    verifierSignatureFileId?: string;
    amountClaimed: number;
    balanceVerifiedOnFx20: boolean;
    balanceAvaliable: number;
    supplierPaymentApprovalStatus?: SupplierPaymentApprovalStatus;
    supplierPaymentLoadedOnBank: boolean;
    supplierStatusComment?: string;
    approvalStatusDoneByUserId?: string;
    approvalSignatureFileId?: string;
}

export interface CreditReportInfo {
  url: string
}

export enum PaymentType {
    Wallet = "wallet",
    Manual = "manual",
}

export interface PaymentDto {
    paymentId: string;
    type: PaymentType;
    contractId: string;
    createdDate: string;
    amount: number;
    outstandingAmount: number;
    dailyCollectionAmount: number;
    description: string;
    targetSystemTransactionId?: string | null;
}

export interface AddPaymentRequest {
    contractId: string;
    amount: number;
    type: PaymentType;
    description: string;
    paymentReference?: string | null;
}
